import React from 'react';
import { motion, type Variants } from 'framer-motion';
import {
    Stethoscope,
    Calculator,
    Droplets,
    Flame,
    Scale,
    LineChart,
    BrainCircuit,
    CheckCircle2,
    ArrowRight,
} from 'lucide-react';

const fadeUp: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.45,
            ease: 'easeOut',
        },
    },
};

const SCIENCE_CARDS = [
    {
        icon: Calculator,
        title: 'Daily Calorie Targeting',
        desc:
            'LesslyKilo estimates your daily energy needs using standard body-size and activity-based logic, then applies a sustainable calorie target based on your goal. For weight loss, the app focuses on a practical deficit rather than aggressive restriction.',
        accent: 'cyan',
    },
    {
        icon: Flame,
        title: 'Burn & Deficit Logic',
        desc:
            'The app combines logged workouts, movement, and step-based activity estimates to approximate active burn. That helps show whether your day is trending toward maintenance, gain, or the deficit needed for weight loss.',
        accent: 'emerald',
    },
    {
        icon: Droplets,
        title: 'Hydration Goal Estimation',
        desc:
            'Water goals are not treated as random numbers. LesslyKilo uses body-size and activity-aware logic to set a realistic hydration target that supports performance, appetite control, and day-to-day consistency.',
        accent: 'cyan',
    },
    {
        icon: Scale,
        title: 'Body Composition Estimates',
        desc:
            'Where enough profile data is available, LesslyKilo can estimate body fat percentage and related composition signals using established anthropometric logic. These values are treated as estimates, not as a replacement for clinical body composition scans.',
        accent: 'emerald',
    },
    {
        icon: LineChart,
        title: 'Weight Trend Interpretation',
        desc:
            'Single weigh-ins can be noisy. The app looks at trends over time so users can better understand whether changes are likely related to intake, activity, hydration, or normal short-term fluctuation.',
        accent: 'cyan',
    },
    {
        icon: BrainCircuit,
        title: 'Behavior Change Layer',
        desc:
            'Science matters, but behavior is what makes progress stick. LesslyKilo turns complex targets into simple daily actions, feedback, and nudges that help users build consistency instead of relying on motivation alone.',
        accent: 'emerald',
    },
];

const FORMULAS = [
    'Daily calories: baseline needs + activity context + goal adjustment',
    'Weight loss targets: sustainable deficit over extreme restriction',
    'Hydration goals: body-size aware targets with activity context',
    'Active burn: step and workout-based energy estimates',
    'Body fat %: estimated from available profile inputs, not a scan',
    'Progress: trend-based interpretation, not single-day noise',
];

export default function ClinicalLogic() {
    return (
        <section id="medical" className="relative overflow-hidden py-24 md:py-32">
            {/* Background */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.10),transparent_32%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(16,185,129,0.08),transparent_28%)]" />
                <div className="absolute left-[-8%] top-20 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="absolute right-[-10%] bottom-10 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
                <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:48px_48px]" />
            </div>

            <div className="relative mx-auto max-w-7xl px-6">
                {/* Header */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    className="mx-auto mb-16 max-w-4xl text-center"
                >
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-primary)]/20 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(16,185,129,0.08))] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.22em] text-[var(--color-brand-primary)] shadow-[0_8px_30px_rgba(34,211,238,0.08)]">
                        <Stethoscope size={14} />
                        Science-informed methodology
                    </div>

                    <h2 className="text-4xl font-black leading-[1.05] text-[var(--color-brand-text)] md:text-6xl">
                        LesslyKilo uses practical science to make
                        <span className="block bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] bg-clip-text text-transparent">
              everyday decisions easier
            </span>
                    </h2>

                    <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-[var(--color-brand-text-secondary)] md:text-xl">
                        The app does more than log numbers. It uses structured health and behavior logic to estimate
                        calorie needs, hydration targets, energy expenditure, body composition signals, and progress trends
                        so users can make better day-to-day choices.
                    </p>

                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        {[
                            'Calorie targets with goal logic',
                            'Hydration and burn estimation',
                            'Trend analysis over time',
                        ].map((item) => (
                            <div
                                key={item}
                                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(255,255,255,0.02))] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)]"
                            >
                                <CheckCircle2 size={16} className="text-emerald-300" />
                                {item}
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Featured explainer */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    className="mb-12 rounded-[2.5rem] border border-[var(--color-brand-primary)]/12 bg-[linear-gradient(180deg,var(--color-brand-card),var(--color-brand-ai-bg))] p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)] md:p-10"
                >
                    <div className="grid gap-8 lg:grid-cols-[1.2fr_0.9fr] lg:items-center">
                        <div>
                            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-cyan-300">
                                <Calculator size={13} />
                                How the app thinks
                            </div>

                            <h3 className="text-3xl font-black text-[var(--color-brand-text)] md:text-4xl">
                                A better weight app is not just a diary.
                            </h3>

                            <p className="mt-5 text-[15px] leading-8 text-[var(--color-brand-text-secondary)] md:text-base">
                                LesslyKilo is designed to translate health data into useful direction. Instead of simply recording
                                food, water, workouts, and weight, the app estimates what your body likely needs, how your day is
                                tracking against your goal, and where the next best adjustment should happen.
                            </p>

                            <p className="mt-4 text-[15px] leading-8 text-[var(--color-brand-text-secondary)] md:text-base">
                                That means users get more than logs — they get context around calorie targets, hydration,
                                active burn, progress trends, and behavior patterns that influence real results.
                            </p>
                        </div>

                        <div className="rounded-[2rem] border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)]/80 p-6">
                            <div className="mb-4 text-xs font-black uppercase tracking-[0.18em] text-[var(--color-brand-text-secondary)]">
                                Science layers in LesslyKilo
                            </div>

                            <ul className="space-y-3">
                                {FORMULAS.map((item) => (
                                    <li key={item} className="flex items-start gap-3">
                                        <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary)]">
                                            <CheckCircle2 size={12} />
                                        </div>
                                        <span className="text-sm leading-7 text-[var(--color-brand-text-secondary)]">
                      {item}
                    </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </motion.div>

                {/* Science cards */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {SCIENCE_CARDS.map((card, idx) => {
                        const Icon = card.icon;
                        const isCyan = card.accent === 'cyan';

                        return (
                            <motion.div
                                key={card.title}
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ delay: idx * 0.06 }}
                                className="group relative overflow-hidden rounded-[2rem] border border-[var(--color-brand-border)] bg-[linear-gradient(180deg,var(--color-brand-card),var(--color-brand-ai-bg))] p-8 shadow-[0_18px_50px_rgba(15,23,42,0.08)] transition-all hover:border-[var(--color-brand-primary)]/25 hover:translate-y-[-4px]"
                            >
                                <div
                                    className={`pointer-events-none absolute inset-0 ${
                                        isCyan
                                            ? 'bg-[linear-gradient(135deg,rgba(34,211,238,0.06),transparent_45%)]'
                                            : 'bg-[linear-gradient(135deg,rgba(16,185,129,0.06),transparent_45%)]'
                                    }`}
                                />

                                <div className="relative z-10">
                                    <div
                                        className={`mb-6 flex h-14 w-14 items-center justify-center rounded-2xl ${
                                            isCyan
                                                ? 'bg-cyan-400/10 text-cyan-300 border border-cyan-400/15'
                                                : 'bg-emerald-400/10 text-emerald-300 border border-emerald-400/15'
                                        }`}
                                    >
                                        <Icon size={26} />
                                    </div>

                                    <h3 className="text-xl font-black text-[var(--color-brand-text)]">
                                        {card.title}
                                    </h3>

                                    <p className="mt-4 text-sm leading-8 text-[var(--color-brand-text-secondary)]">
                                        {card.desc}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Bottom note / CTA */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    className="mt-14 rounded-[2.5rem] border border-[var(--color-brand-primary)]/12 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(16,185,129,0.06),rgba(255,255,255,0.02))] p-8 shadow-[0_18px_50px_rgba(15,23,42,0.08)] md:p-10"
                >
                    <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
                        <div className="max-w-3xl">
                            <h3 className="flex items-center gap-3 text-2xl font-black text-[var(--color-brand-text)] md:text-3xl">
                                <BrainCircuit size={28} className="text-[var(--color-brand-primary)]" />
                                Science is only useful when it changes behavior
                            </h3>
                            <p className="mt-4 text-sm leading-8 text-[var(--color-brand-text-secondary)] md:text-base">
                                LesslyKilo combines physiology-aware estimates with behavior design, so users do not just see targets —
                                they get daily guidance that helps them act on those targets consistently.
                            </p>
                        </div>

                        <a
                            href="#features"
                            className="inline-flex items-center gap-2 rounded-2xl bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] px-8 py-4 text-sm font-black text-white shadow-[0_12px_28px_rgba(34,211,238,0.22)] transition-all hover:scale-[1.02] hover:brightness-110"
                        >
                            Explore the features
                            <ArrowRight size={16} />
                        </a>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}